import { computed, ref, Ref } from 'vue';
import { getSortedRowModel, SortingState } from '@tanstack/vue-table';
import { merge, isNil } from 'lodash-es';
import { Virtualizer } from '@tanstack/vue-virtual';
import type { ContextMenuItems } from '@/common/utils';
import { compareArrayFieldValues } from '@/common/utils/commonUtils';
import { normalizeAgeToSeconds } from '@/common/components/molecules/filterSearch/filterSearch.specificFilterFunctions';
import { useEnvStore } from '../core/useEnvStore';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridRowInstance,
  GridColumnInstance,
  GridSortingOption,
} from '../../modernGrid.types';
import { getDefaultSortingOption } from '../../modernGrid.define';
import { isChangedEnv } from '../../modernGrid.utils';

const isArrayEmpty = (v: unknown): boolean =>
  Array.isArray(v) && (v.length === 0 || v.every((item) => item == null || item === ''));

export const useSorting = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  virtualRowManager,
  envKey,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  virtualRowManager: Ref<Virtualizer<HTMLElement, Element>>;
  envKey: Ref<string | undefined>;
}) => {
  const { getEnv, saveEnv } = useEnvStore(props.useDashboardStore ?? false);
  const sortingInfo = computed<GridSortingOption>(() =>
    merge({}, getDefaultSortingOption(), props.option?.sorting),
  );
  const sortingState = ref<SortingState>([]);

  const onSortColumn = (column: GridColumnInstance<RowDataType> | null, isDesc: boolean): void => {
    if (!column) {
      return;
    }
    column.toggleSorting(isDesc, false);
  };

  const createSortingMenuItems = (column: GridColumnInstance<RowDataType>): ContextMenuItems[] => {
    const { ascending, descending } = sortingInfo.value.columnMenu || {};
    return [
      !!ascending?.use && {
        text: ascending?.title || '',
        iconClass: ascending?.icon || '',
        disabled: !column.getCanSort(),
        click: () => onSortColumn(column, false),
      },
      !!descending?.use && {
        text: descending?.title || '',
        iconClass: descending?.icon || '',
        disabled: !column.getCanSort(),
        click: () => onSortColumn(column, true),
      },
    ].filter(Boolean) as ContextMenuItems[];
  };

  const updateSortingState = async (
    updater: SortingState | ((state: SortingState) => SortingState),
  ) => {
    sortingState.value = typeof updater === 'function' ? updater(sortingState.value) : updater;
    virtualRowManager.value?.measure();

    const envData = getEnv(envKey.value);
    if (isChangedEnv(envData?.sorting, sortingState.value, true)) {
      await saveEnv(envKey.value, { ...envData, sorting: sortingState.value });
    }

    emit('change-sorting', sortingState.value);
  };

  const sortingOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => ({
    getSortedRowModel: getSortedRowModel(),
    enableSorting: sortingInfo.value.enable,
    enableMultiSort: sortingInfo.value.multi,
    sortDescFirst: false,
    sortingFns: {
      sortingFnForArray: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        const isEmptyA = a == null || a === '' || isArrayEmpty(a);
        const isEmptyB = b == null || b === '' || isArrayEmpty(b);

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA || isEmptyB) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (isEmptyA) return isDesc ? -1 : 1;
          if (isEmptyB) return isDesc ? 1 : -1;
        }

        // compareArrayFieldValues를 사용하여 배열 내용 기반 정렬
        return compareArrayFieldValues(a, b);
      },
      sortingFnForObjectKeys: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        const keysA =
          a != null && typeof a === 'object' && !Array.isArray(a)
            ? Object.keys(a as object).sort()
            : [];
        const keysB =
          b != null && typeof b === 'object' && !Array.isArray(b)
            ? Object.keys(b as object).sort()
            : [];

        const isEmptyA = keysA.length === 0;
        const isEmptyB = keysB.length === 0;

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA || isEmptyB) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          return isEmptyA ? (isDesc ? -1 : 1) : isDesc ? 1 : -1;
        }

        const minLen = Math.min(keysA.length, keysB.length);
        for (let i = 0; i < minLen; i++) {
          const cmp = keysA[i].localeCompare(keysB[i]);
          if (cmp !== 0) return cmp;
        }
        return keysA.length - keysB.length;
      },
      sortingFnForNumber: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const rawA = rowA.getValue(columnId);
        const rawB = rowB.getValue(columnId);

        const isEmptyA = rawA == null || rawA === '';
        const isEmptyB = rawB == null || rawB === '';

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA || isEmptyB) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (isEmptyA) return isDesc ? -1 : 1;
          if (isEmptyB) return isDesc ? 1 : -1;
        }

        // 숫자 파싱 (콤마 제거)
        const a = typeof rawA === 'number' ? rawA : parseFloat(String(rawA).replace(/,/g, ''));
        const b = typeof rawB === 'number' ? rawB : parseFloat(String(rawB).replace(/,/g, ''));

        if (isNaN(a) && isNaN(b)) return 0;
        if (isNaN(a) || isNaN(b)) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (isNaN(a)) return isDesc ? -1 : 1;
          if (isNaN(b)) return isDesc ? 1 : -1;
        }

        return a - b;
      },
      sortingFnForString: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const rawA = rowA.getValue(columnId);
        const rawB = rowB.getValue(columnId);

        const a = rawA != null ? String(rawA) : '';
        const b = rawB != null ? String(rawB) : '';

        if (a === '' && b === '') return 0;
        if (a === '' || b === '') {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (a === '') return isDesc ? -1 : 1;
          if (b === '') return isDesc ? 1 : -1;
        }

        const lowerA = a.toLowerCase();
        const lowerB = b.toLowerCase();
        if (a !== b && lowerA === lowerB) {
          const diffIndex = Array.from(a).findIndex((char, index) => char !== b[index]);
          return a[diffIndex] < b[diffIndex] ? -1 : 1;
        }
        return lowerA < lowerB ? -1 : lowerA > lowerB ? 1 : 0;
      },
      sortingFnForAge: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        const isEmptyA = a == null || a === '';
        const isEmptyB = b == null || b === '';

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA || isEmptyB) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (isEmptyA) return isDesc ? -1 : 1;
          if (isEmptyB) return isDesc ? 1 : -1;
        }

        if (typeof a !== 'string' || typeof b !== 'string') return 0;
        return normalizeAgeToSeconds(a) - normalizeAgeToSeconds(b);
      },
      sortingFnForDatetime: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        const isEmptyA = a == null || a === '';
        const isEmptyB = b == null || b === '';

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA || isEmptyB) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (isEmptyA) return isDesc ? -1 : 1;
          if (isEmptyB) return isDesc ? 1 : -1;
        }

        return a > b ? 1 : a < b ? -1 : 0;
      },
      sortingFnForBasic: (
        rowA: GridRowInstance<RowDataType>,
        rowB: GridRowInstance<RowDataType>,
        columnId: string,
      ) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        const isEmptyA = a == null || a === '';
        const isEmptyB = b == null || b === '';

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA || isEmptyB) {
          const isDesc = sortingState.value.some((s) => s.id === columnId && s.desc);
          if (isEmptyA) return isDesc ? -1 : 1;
          if (isEmptyB) return isDesc ? 1 : -1;
        }

        return a > b ? 1 : a < b ? -1 : 0;
      },
    },
    onSortingChange: updateSortingState,
  }));

  const setSortingState = () => {
    const envSorting = getEnv(envKey.value)?.sorting;
    if (!isNil(envSorting)) {
      sortingState.value = envSorting;
      table.value?.setSorting(envSorting);
    }
  };

  return {
    sortingInfo,
    sortingState,
    sortingOptions,
    createSortingMenuItems,
    setSortingState,
  };
};
